import ComponentPlaceholder from 'components/ComponentPlaceholder';
import { getValidSitecoreLink } from 'components/SitecoreLink';
import { withRequiredProps } from 'hoc';
import type { DigizuiteAsset } from 'models/asset';
import type { ProductCard } from 'models/productCard';
import type {
	Field,
	SitecoreBlockProps,
	SitecoreLink,
	Theme,
} from 'models/sitecore';
import { getEditorMargin } from 'utils/business-logic';
import { getAsset, is } from 'utils/helpers';

import ThemedProductCarousel from './ThemedProductCarousel';

interface Props extends SitecoreBlockProps {
	fields: {
		datasourceName?: string;
		description?: Field<string>;
		heading?: Field<string>;
		landscapeImage?: Field<DigizuiteAsset[]>;
		link?: Field<SitecoreLink>;
		linksHeading?: Field<string>;
		primaryLink?: Field<SitecoreLink>;
		products?: ProductCard[];
		quaternaryLink?: Field<SitecoreLink>;
		secondaryLink?: Field<SitecoreLink>;
		squareImage?: Field<DigizuiteAsset[]>;
		tertiaryLink?: Field<SitecoreLink>;
		theme?: Theme;
	};
}

function ThemedProductCarouselBlock({ fields, params }: Props) {
	const {
		datasourceName,
		heading,
		landscapeImage,
		primaryLink,
		products,
		quaternaryLink,
		secondaryLink,
		squareImage,
		tertiaryLink,
		theme,
	} = fields;
	const hasProducts = is.arrayWithLength(products);
	const link = getValidSitecoreLink(fields.link?.value);
	const linksHeading = fields.linksHeading?.value;
	const links = [primaryLink, secondaryLink, tertiaryLink, quaternaryLink]
		.map((scLink) => getValidSitecoreLink(scLink?.value))
		.filter(is.truthy);

	if (!hasProducts || !theme?.backgroundColor) {
		return (
			<ComponentPlaceholder
				className={getEditorMargin(params)}
				componentName="ThemedProductCarousel"
				description="Requires products and a theme with a color"
			/>
		);
	}

	const topLeftImage = getAsset('Source Copy', theme.topLeftImageLandscape);
	const centerImageLarge = getAsset('Source Copy', squareImage);
	const centerImageSmall = getAsset('Source Copy', landscapeImage);

	return (
		<ThemedProductCarousel
			// Assume both images will have the same meaning.
			centerImageAlt={centerImageLarge?.alt || centerImageSmall?.alt || ''}
			centerImageLargeSrc={centerImageLarge?.src}
			centerImageSmallSrc={centerImageSmall?.src}
			className={getEditorMargin(params)}
			color={theme.backgroundColor}
			gtmItemListId="block"
			gtmItemListName={`Block: ${datasourceName ?? 'ThemedProductCarouselUnknown'}`}
			heading={heading}
			headingLevel={params?.heading}
			id={params?.anchor}
			link={link}
			products={products}
			text={fields.description}
			topLeftImageSrc={topLeftImage?.src}
			linksHeading={linksHeading}
			links={links}
		/>
	);
}
ThemedProductCarouselBlock.displayName = 'ThemedProductCarouselBlock';

export default withRequiredProps(ThemedProductCarouselBlock, 'fields');
